import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../state/state.interface';
import { TEditItem, TItemFieldThatCanBeRecursivelyUpdated } from './items-modal.interface';

interface IRecursiveUpdatePayload {
  fieldsToUpdate: Partial<Pick<TEditItem, TItemFieldThatCanBeRecursivelyUpdated>>;
  itemRecurrenceId: number;
  startDate: string | null; // Provide null if all recurring items are updated
}

interface IRecurringItemStoreState {
  updateLoadStatus: ELoadStatus;
}

export type TCronScheduleType = 'daily' | 'weekly' | 'monthly';

@Injectable()
export class RecurringItemStore extends ComponentStore<IRecurringItemStoreState> {
  public readonly update = this.effect((trigger$: Observable<IRecursiveUpdatePayload>) =>
    trigger$.pipe(
      switchMap((params) => {
        if (!Object.keys(params.fieldsToUpdate).length) {
          return EMPTY;
        }

        this.patchState({ updateLoadStatus: ELoadStatus.loading });

        return this.updateRecurring(params).pipe(
          tapResponse(
            () =>
              this.patchState({
                updateLoadStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(private readonly http: HttpClient) {
    super({
      updateLoadStatus: ELoadStatus.initial,
    });
  }

  private updateRecurring(params: IRecursiveUpdatePayload): Observable<unknown> {
    return this.http.patch<unknown>('items/update-recurring', params);
  }

  public static getCronSchedule(cronExpression: string): TCronScheduleType {
    const CRON_PARTS_LENGTH = 5;
    let output: TCronScheduleType = 'daily';

    try {
      const parts = cronExpression.trim().split(/\s+/);

      if (parts.length !== CRON_PARTS_LENGTH) {
        return output;
      }

      const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

      if (RecurringItemStore.isSpecificDayOfMonth(dayOfMonth)) {
        output = 'monthly';
      } else if (RecurringItemStore.isSpecificWeekdayOfMonth(dayOfWeek)) {
        output = 'monthly';
      } else if (RecurringItemStore.isSpecificDayOfWeek(dayOfWeek)) {
        output = 'weekly';
      }
    } catch {
      output = 'daily';
    }

    return output;
  }

  private static isSpecificDayOfMonth(dayOfMonth: string): boolean {
    const excludedPatterns = ['*', '1/1', '-', ',', '/'];

    return !excludedPatterns.some((pattern) => dayOfMonth.includes(pattern));
  }

  private static isSpecificWeekdayOfMonth(dayOfWeek: string): boolean {
    return dayOfWeek.includes('#');
  }

  private static isSpecificDayOfWeek(dayOfWeek: string): boolean {
    const excludedPatterns = ['*', '1/1', '-'];

    return !excludedPatterns.some((pattern) => dayOfWeek.includes(pattern));
  }
}
